import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { NavigateFunction, NavigateOptions, To } from 'react-router-dom';

export const useNavigatePreserveQueryParams = (allowedParams?: string[]) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigatePreserveQueryParams: NavigateFunction = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      const filteredParams = new URLSearchParams();

      if (allowedParams) {
        allowedParams.forEach((param) => {
          if (searchParams.has(param)) {
            filteredParams.set(param, searchParams.get(param)!);
          }
        });
      } else {
        searchParams.forEach((value, key) => {
          filteredParams.set(key, value);
        });
      }

      if (typeof to === 'string') {
        const [path, queryString] = to.split('?');
        const newParams = new URLSearchParams(queryString);

        newParams.forEach((value, key) => {
          filteredParams.set(key, value);
        });

        navigate(`${path}?${filteredParams.toString()}`, options);
      } else {
        const { search } = to;
        const newParams = new URLSearchParams(search);

        newParams.forEach((value, key) => {
          filteredParams.set(key, value);
        });

        navigate({ ...to, search: `?${filteredParams.toString()}` }, options);
      }
    },
    [location, navigate, allowedParams]
  );

  return { navigatePreserveQueryParams };
};
